import React from 'react';

import {
  TbArrowsSplit2,
  TbCircleCheck,
  TbClock,
  TbDots,
  TbFileDescription,
  TbHomeHeart,
  TbInfoCircle,
  TbPencil,
  TbReload,
  TbUser,
  TbUserOff,
  TbUserX,
  TbWorldHeart,
  TbX,
} from 'react-icons/tb';

import { createLocale } from 'locales';

import type { OrganizationJob } from 'state/remote/types';

import Icon from 'components-new/icon/icon';
import DropdownMenu from 'components-new/dropdown-menu/dropdown-menu';
import type { DropdownMenuItemType } from 'components-new/dropdown-menu/dropdown-menu-item';
import IconButton from 'components-new/icon-button/icon-button';

import useJobPopover from './use-job-popover';
import CancelJobDialog from './cancel-job-dialog';
import FeedbackDialog from './feedback-dialog';
import NoshowDialog from './noshow-dialog';

const localize = createLocale('organizationWeekPlannerLocales');

interface JobPopoverProps {
  job: OrganizationJob;
  button?: React.ReactNode;
  actionsToDelete?: Array<string>;
}

const JobPopover: React.FC<JobPopoverProps> = (props) => {
  const { job, actionsToDelete } = props;

  const jobPopover = useJobPopover(job);

  const actions = [
    {
      key: '1',
      label: localize('jobPopoverAssignmentDetails'),
      leadingElement: <Icon icon={TbInfoCircle} className="text-gray700" />,
      onClick: jobPopover.onAssignmentDetailsClicked,
      showAction: !actionsToDelete?.includes('assignment-details'),
    },
    {
      key: '2',
      label: localize('jobPopoverConfirm'),
      leadingElement: <Icon icon={TbCircleCheck} className="text-gray700" />,
      onClick: jobPopover.onConfirmClicked,
      showAction:
        jobPopover.displayConfirmAction &&
        !actionsToDelete?.includes('confirm'),
    },
    // {
    //   key: '3',
    //   label: localize('jobPopoverAssignFreelancer'),
    //   leadingElement: (
    //     <Icon
    //       icon={job.is_location_favorite ? TbHeartFilled : TbHeart}
    //       className={job.is_favorite ? 'text-destructive' : 'text-gray700'}
    //     />
    //   ),
    //   onClick: jobPopover.onAssignJobToFreelancerClicked,
    //   showAction:
    //     jobPopover.disaplyAssignFreelancerAction &&
    //     !actionsToDelete?.includes('assign-freelancer'),
    // },
    {
      key: '4',
      label: localize('jobPopoverConvertToUrgent'),
      leadingElement: <Icon icon={TbUser} className="text-gray700" />,
      onClick: jobPopover.onInviteEveryoneClicked,
      showAction:
        jobPopover.displayConvertToUrgentAction &&
        !actionsToDelete?.includes('invite-everyone'),
    },
    {
      key: '5',
      label: localize('jobPopoverEdit'),
      leadingElement: <Icon icon={TbPencil} className="text-gray700" />,
      onClick: jobPopover.onEditClicked,
      showAction:
        jobPopover.displayEditAction && !actionsToDelete?.includes('edit'),
    },
    {
      key: '6',
      label: localize('jobPopoverHours'),
      leadingElement: <Icon icon={TbClock} className="text-gray700" />,
      onClick: jobPopover.onHoursClicked,
      showAction:
        jobPopover.displayHoursAction && !actionsToDelete?.includes('hours'),
    },
    /* {
      key: '7',
      label: localize('jobPopoverFeedback'),
      leadingElement: <Icon icon={TbStar} className="text-gray700" />,
      onClick: jobPopover.onFeedbackClicked,
      showAction:
        jobPopover.displayFeedbackAction &&
        !actionsToDelete?.includes('feedback'),
    }, */
    {
      key: '8',
      label: localize('jobPopoverCancelAssignment'),
      leadingElement: <Icon icon={TbX} />,
      onClick: jobPopover.onCancelClicked,
      showAction:
        jobPopover.displayCancelAction &&
        !actionsToDelete?.includes('cancel-assignment'),
      variant: 'destructive',
    },
    {
      key: '9',
      label: localize('jobPopoverNoShow'),
      leadingElement: <Icon icon={TbUserX} className="text-gray700" />,
      onClick: jobPopover.onNoShowClicked,
      showAction:
        jobPopover.displayNoShowAction && !actionsToDelete?.includes('noshow'),
    },
    {
      key: '10',
      label: localize('jobPopoverRepeatAssignment'),
      leadingElement: <Icon icon={TbReload} className="text-gray700" />,
      onClick: jobPopover.onRepeatAssignmentsClicked,
      showAction:
        !!job.assigned_to && !actionsToDelete?.includes('repeat-assignment'),
    },
    {
      key: '11',
      label: localize('jobPopoverSplit'),
      leadingElement: <Icon icon={TbArrowsSplit2} className="text-gray700" />,
      onClick: jobPopover.onSplitClicked,
      showAction:
        jobPopover.displaySplitAction && !actionsToDelete?.includes('split'),
    },
    {
      key: '12',
      label: localize('jobPopoverAgreement'),
      leadingElement: (
        <Icon icon={TbFileDescription} className="text-gray700" />
      ),
      onClick: jobPopover.onAgreementClicked,
      showAction: !!job.assigned_to && !actionsToDelete?.includes('agreement'),
    },
  ];

  const dangerActions = [
    {
      key: '1',
      label: job.is_favorite
        ? localize('jobPopoverUnfavoriteFreelancerForOrganization')
        : localize('jobPopoverFavoriteFreelancerForOrganization'),
      leadingElement: (
        <Icon
          icon={TbWorldHeart}
          className={job.is_favorite ? 'text-destructive' : 'text-gray700'}
        />
      ),
      onClick: job.is_favorite
        ? jobPopover.onUnfavoriteFreelancerForOrganizationClicked
        : jobPopover.onFavoriteFreelancerForOrganizationClicked,
      showAction:
        !!job.assigned_to &&
        !actionsToDelete?.includes(
          'favorite/unfavorite-freelancer-in-organization',
        ),
      variant: job.is_favorite ? 'destructive' : 'normal',
    },
    {
      key: '2',
      label: job.is_location_favorite
        ? localize('jobPopoverUnfavoriteFreelancerForLocation')
        : localize('jobPopoverFavoriteFreelancerForLocation'),
      leadingElement: (
        <Icon
          icon={TbHomeHeart}
          className={
            job.is_location_favorite ? 'text-destructive' : 'text-gray700'
          }
        />
      ),
      onClick: job.is_location_favorite
        ? jobPopover.onUnfavoriteFreelancerForLocationClicked
        : jobPopover.onFavoriteFreelancerForLocationClicked,
      showAction:
        !!job.assigned_to &&
        !actionsToDelete?.includes(
          'favorite/unfavorite-freelancer-in-location',
        ),
      variant: job.is_location_favorite ? 'destructive' : 'normal',
    },
    {
      key: '3',
      label: localize('jobPopoverBlockFreelancerFromOrganization'),
      leadingElement: <Icon icon={TbUserOff} />,
      onClick: jobPopover.onBlockFreelancerFromOrganizationClicked,
      showAction:
        !!job.assigned_to &&
        !job.is_blacklisted &&
        !actionsToDelete?.includes('block-freelancer-from-organization'),
      variant: 'destructive',
    },
    {
      key: '4',
      label: localize('jobPopoverUnblockFreelancerFromOrganization'),
      leadingElement: <Icon icon={TbUserOff} className="text-gray700" />,
      onClick: jobPopover.onUnblockFreelancerFromOrganizationClicked,
      showAction:
        !!job.assigned_to &&
        job.is_blacklisted &&
        !actionsToDelete?.includes('unblock-freelancer-from-organization'),
    },
    {
      key: '5',
      label: localize('jobPopoverBlockFreelancerInLocation'),
      leadingElement: <Icon icon={TbUserOff} />,
      onClick: jobPopover.onBlockFreelancerInLocationClicked,
      showAction:
        !!job.assigned_to &&
        !job.is_blacklisted_by_location &&
        !actionsToDelete?.includes('block-freelancer-in-location'),
      variant: 'destructive',
    },
    {
      key: '6',
      label: localize('jobPopoverUnblockFreelancerInLocation'),
      leadingElement: <Icon icon={TbUserOff} className="text-gray700" />,
      onClick: jobPopover.onUnblockFreelancerInLocationClicked,
      showAction:
        !!job.assigned_to &&
        job.is_blacklisted_by_location &&
        !actionsToDelete?.includes('unblock-freelancer-from-location'),
    },
  ];

  const dropdownMenuItems: DropdownMenuItemType[] = actions
    .concat(dangerActions)
    .filter((action) => !!action.showAction)
    .map((action) => ({
      ...action,
      variant: action.variant === 'destructive' ? 'destructive' : 'normal',
    }));

  return (
    <>
      <DropdownMenu
        autoplacement
        strategy="fixed"
        menuItemsClassName="z-50"
        items={dropdownMenuItems}
        menuButton={() => props.button || <IconButton icon={TbDots} />}
      />
      {jobPopover.isCancelDialogOpen && (
        <CancelJobDialog
          isOpen={jobPopover.isCancelDialogOpen}
          onAcceptClicked={jobPopover.onCancelDialogAcceptClicked}
          onBackClicked={() => jobPopover.setIsCancelDialogOpen(false)}
          onCloseClicked={() => jobPopover.setIsCancelDialogOpen(false)}
          variant={jobPopover.cancelJobDialogVariant}
          job={{
            competence: job.competence,
            date: job.date,
            end: job.end,
            freelancer: job.assigned_to?.name,
            location: job.location.name,
            start: job.start,
          }}
        />
      )}
      {jobPopover.isFeedbackDialogOpen && (
        <FeedbackDialog
          isOpen={jobPopover.isFeedbackDialogOpen}
          onAcceptClicked={jobPopover.onFeedbackDialogAcceptClicked}
          onCloseClicked={() => jobPopover.setIsFeedbackDialogOpen(false)}
          job={{
            date: job.date,
            freelancer: job.assigned_to?.name,
            id: job.id,
          }}
          onBackClicked={() => jobPopover.setIsFeedbackDialogOpen(false)}
        />
      )}
      {jobPopover.isNoshowDialogOpen && (
        <NoshowDialog
          isOpen={jobPopover.isNoshowDialogOpen}
          onAcceptClicked={jobPopover.onNoshowDialogAcceptClicked}
          onBackClicked={() => jobPopover.setIsNoshowDialogOpen(false)}
          onCloseClicked={() => jobPopover.setIsNoshowDialogOpen(false)}
          job={{
            date: job.date,
            end: job.end,
            freelancer: job.assigned_to?.name,
            location: job.location.name,
            start: job.start,
          }}
        />
      )}
    </>
  );
};

export default JobPopover;
